import type { Appearance } from '@stripe/stripe-js';
import { Skeleton } from 'antd';
import React, { FunctionComponent, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { Color } from '../../../config/Color';
import { PurchaseSelector } from '../../../store/purchase/PurchaseActions';
import { lock, tagGreen } from '../../assets/images/Icons';
import { usePlanSummary } from '../CheckoutInfo/PlanSummaryTableForm';
import { useCouponDiscount } from '../Header/Coupon';

/**
 * The redesign's white checkout card: the price summary, then the email or Continue step and the
 * card form passed in as children.
 */
export const RedesignCheckoutPanel: FunctionComponent<
  PropsWithChildren<{ checkoutShown: boolean }>
> = (props) => (
  <Panel data-test="redesignCheckoutPanel">
    <PriceSummary />
    {props.children}
    {props.checkoutShown || (
      <Reassurance>
        <img src={lock} alt="" width={10} height={13} />
        <span>Secure checkout · Cancel any time</span>
      </Reassurance>
    )}
  </Panel>
);

/** The same prices, coupon discounts and fine print as Control's `PlanSummaryTableForm`. */
const PriceSummary: FunctionComponent = () => {
  const { purchase, hasTrial, prices, finePrint, discountedBilling, planInterval } =
    usePlanSummary();
  const couponDiscount = useCouponDiscount();

  if (prices === undefined) {
    return <Skeleton title={{ width: '100%' }} paragraph={{ rows: 2 }} active />;
  }

  const { totalPriceToday, trialPlanPrice, initialWeeklyPlanPrice, strikethroughWeeklyPrice } =
    prices;
  // Any applied coupon is shown here; the redesign has no "discount applied" banner.
  const discountRow = couponDiscount !== undefined && (
    <DiscountRow data-test="couponDiscount">
      <span>
        <img src={tagGreen} alt="" width={16} height={16} />
        Discount applied
      </span>
      <span>−{couponDiscount}</span>
    </DiscountRow>
  );

  return (
    <>
      <TotalRow data-test="todayPrice">
        <TotalLabel>Total today</TotalLabel>
        <TotalPrice data-test="price">{totalPriceToday.toString()}</TotalPrice>
      </TotalRow>
      {!hasTrial && discountRow}
      {hasTrial && (
        <Breakdown>
          <Row data-test="trialPrice">
            <span>
              {purchase.trialPeriodDays}-day {PurchaseSelector.hasFreeTrial(purchase) && 'free '}
              trial
            </span>
            <span data-test="price">{trialPlanPrice.toString()}</span>
          </Row>
          {discountRow}
          <Row data-test="introPrice">
            <span>Price after trial</span>
            <span>
              {strikethroughWeeklyPrice !== undefined && (
                <Struck data-test="strikethroughPrice">
                  {strikethroughWeeklyPrice.toString()}
                </Struck>
              )}
              <Strong data-test="price">{initialWeeklyPlanPrice.toString()}/week</Strong>
            </span>
          </Row>
          <FinePrint data-test="finePrint">
            {discountedBilling !== undefined ? (
              <>
                Billed at <s>{discountedBilling.regular.toString()}</s>{' '}
                {discountedBilling.ongoing.toString()} every {planInterval?.inText}
              </>
            ) : (
              finePrint
            )}
          </FinePrint>
        </Breakdown>
      )}
    </>
  );
};

/** The card is white on Nerva's dark page, so it takes the light-surface tokens. */
const card = {
  background: Color.subscription.accent,
  text: Color.decorative.accent,
  textMuted: Color.state.select,
  field: Color.action.muted,
  border: Color.action.accent,
};

/** Stripe's card fields styled to match the card and its email field. */
export function cardStripeAppearance(fontFamily: string): Appearance {
  const inputBorder = `1px solid ${card.border}`;
  return {
    theme: 'stripe',
    variables: {
      fontFamily,
      fontSizeBase: '14px',
      spacingUnit: '4px',
      colorBackground: card.field,
      colorPrimary: Color.action.default,
      colorText: card.text,
      colorDanger: Color.state.error,
      borderRadius: '14px',
    },
    rules: {
      '.Tab': { border: inputBorder, boxShadow: 'none', backgroundColor: card.background },
      '.Tab--selected': { border: `1.5px solid ${Color.action.default}`, boxShadow: 'none' },
      '.Input': { border: inputBorder, padding: '16px', boxShadow: 'none' },
      '.Input:focus': { border: `2px solid ${Color.action.default}`, boxShadow: 'none' },
      '.Label': { fontSize: '13px', fontWeight: '500', color: card.textMuted },
    },
  };
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing._16px};
  padding: ${({ theme }) => `${theme.spacing._24px} ${theme.spacing._20px} ${theme.spacing._20px}`};
  border-radius: 20px;
  background: ${card.background};
  color: ${card.text};
  text-align: left;

  /*
   * Shared widgets the card can't style through props: the "Why now?" disclosure, the small print,
   * and antd's email field and buttons. Everything else in the forms inherits the card's colour.
   */
  [data-test='whyNow'] {
    font-size: 14px;
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    color: ${Color.action.default};
  }
  [data-test='whyNowSection'] {
    border-radius: 14px;
    background: ${card.field};
    color: ${card.textMuted};
    font-size: 14px;
  }
  [data-test='disclaimer'],
  label {
    color: ${card.textMuted};
  }
  .ant-form-item {
    margin-bottom: ${({ theme }) => theme.spacing._12px};
  }
  .ant-input,
  .ant-input[disabled],
  .ant-input[disabled]:hover {
    height: 52px;
    padding: ${({ theme }) => `0 ${theme.spacing._16px}`};
    border-radius: 14px;
    border-color: ${card.border};
    background: ${card.field};
    color: ${card.text};
    font-size: 16px;
    text-align: left;
  }
  .ant-btn-primary {
    height: 56px;
    border-radius: 999px;
  }
  .ant-btn-primary:not([disabled]),
  .ant-btn-primary:not([disabled]):hover,
  .ant-btn-primary:not([disabled]):focus {
    border-color: ${Color.action.default};
    background: ${Color.action.default};
    /* The loading spinner draws in the text colour, so keep it visible on the green. */
    color: ${card.background};
    box-shadow: none;
  }
  /* antd veils loading buttons in 35% white, which washes out the white label on green. */
  .ant-btn-primary.ant-btn-loading::before {
    display: none;
  }
  .ant-btn-primary:not([disabled]) p {
    /* NextButton forces its label colour with !important. */
    color: ${card.background} !important;
    font-weight: ${({ theme }) => theme.fontWeight.medium};
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const TotalLabel = styled.span`
  color: ${card.text};
  font-size: 17px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const TotalPrice = styled(TotalLabel)`
  color: ${Color.action.default};
`;

const Breakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing._10px};
  padding: ${({ theme }) => `${theme.spacing._14px} 0`};
  border-top: 0.6px solid ${card.border};
  border-bottom: 0.6px solid ${card.border};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 15px;
  color: ${card.textMuted};
`;

const DiscountRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._12px};
  padding: ${({ theme }) => `${theme.spacing._10px} ${theme.spacing._12px}`};
  border-radius: 12px;
  background: ${Color.state.successMuted};
  color: ${card.text};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};

  span {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing._8px};
  }
`;

const Struck = styled.s`
  margin-right: ${({ theme }) => theme.spacing._6px};
`;

// Regular weight so "Total today" stays the loudest figure in the card.
const Strong = styled.span`
  color: ${card.text};
`;

const FinePrint = styled.span`
  align-self: flex-end;
  font-size: 13px;
  color: ${card.textMuted};
`;

const Reassurance = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing._6px};
  font-size: 13px;
  color: ${card.textMuted};
`;
